import { z } from 'zod';

import { ScheduleNotifyInput, ScheduleOnEndInput } from '@nucleus-commerce/api-client/admin';

import { rulesFormSchema } from '@/features/product-rules';

/** Caps the backend enforces (D-181 decision 4). */
export const MAX_SCHEDULE_STORES = 50;
export const MAX_LIVE_COUNT = 1000;
export const MAX_INTERVAL_DAYS = 365;
export const MAX_CYCLES = 10_000;
export const MAX_SCHEDULE_MEMBERS = 5000;

const RUN_AT = /^([01]\d|2[0-3]):[0-5]\d$/;
const LOCAL_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const WHOLE = /^\d+$/;

const wholeIn = (min: number, max: number, error: string) =>
  z
    .string()
    .trim()
    .refine((v) => WHOLE.test(v) && Number(v) >= min && Number(v) <= max, { error });

export const scheduleStoreFormSchema = z.object({
  storeId: z.string().min(1),
  redirectCollectionId: z.string().nullable(),
  /** Shown beside the picker; never sent. */
  redirectCollectionTitle: z.string().nullable(),
});

export const poolProductFormSchema = z.object({
  id: z.string().min(1),
  /** Null until looked up, for ids handed over in the URL. */
  title: z.string().nullable(),
  pinned: z.boolean(),
});

/**
 * The rotation as the create page and the Settings tab edit it. Numbers stay strings until the
 * mapper; `startsAt` / `endsAt` are `datetime-local` values on the wall clock of `timeZone`, so
 * comparing the two strings compares the instants. `products` is the pool picked on the create
 * page; after that the Pool tab owns the members.
 */
export const scheduleFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, { error: 'schedules:validation.nameRequired' })
      .max(120, { error: 'schedules:validation.nameLength' }),
    stores: z
      .array(scheduleStoreFormSchema)
      .max(MAX_SCHEDULE_STORES, { error: 'schedules:validation.storesMax' }),
    liveCount: wholeIn(1, MAX_LIVE_COUNT, 'schedules:validation.liveCount'),
    intervalDays: wholeIn(1, MAX_INTERVAL_DAYS, 'schedules:validation.intervalDays'),
    runAt: z.string().regex(RUN_AT, { error: 'schedules:validation.runAt' }),
    timeZone: z.string().trim().min(1, { error: 'schedules:validation.timeZoneRequired' }),
    startsAt: z.string().regex(LOCAL_DATETIME, { error: 'schedules:validation.startsAtRequired' }),
    endsAt: z.union([z.literal(''), z.string().regex(LOCAL_DATETIME)]),
    maxCycles: z.union([z.literal(''), wholeIn(1, MAX_CYCLES, 'schedules:validation.maxCycles')]),
    loop: z.boolean(),
    onEnd: z.enum(ScheduleOnEndInput),
    notify: z.enum(ScheduleNotifyInput),
    rulesEnabled: z.boolean(),
    rules: rulesFormSchema,
    products: z
      .array(poolProductFormSchema)
      .max(MAX_SCHEDULE_MEMBERS, { error: 'schedules:validation.poolMax' }),
  })
  .refine((v) => v.endsAt === '' || v.endsAt > v.startsAt, {
    path: ['endsAt'],
    error: 'schedules:validation.endsAfterStart',
  })
  .refine((v) => !v.rulesEnabled || v.rules.conditions.length > 0, {
    path: ['rules', 'conditions'],
    error: 'schedules:validation.conditionsRequired',
  });

export type ScheduleFormInput = z.input<typeof scheduleFormSchema>;
export type ScheduleFormOutput = z.output<typeof scheduleFormSchema>;
export type ScheduleStoreForm = z.infer<typeof scheduleStoreFormSchema>;
export type PoolProductForm = z.infer<typeof poolProductFormSchema>;
